/**
 * Release notes shown on /changelog. Add objects when you publish updates, newest first.
 *
 * Presentation convention (when rendering or writing copy):
 * - Critical updates: set critical: true. Rendered bold red, each as its own entry
 *   (do not merge with other bullets in the same line).
 * - Other updates: normal text; several changes can be grouped in one entry or paragraph.
 *
 * Example:
 * {
 *   date: '2026-05-01',
 *   version: '0.3',
 *   appId: 'slab_calculator',
 *   title: 'Short headline',
 *   body: 'Optional longer description (markdown not supported unless you add it).',
 *   critical: false,
 * }
 */
export const CHANGELOG_ENTRIES = [
  {
    date: '2026-10-04',
    version: '0.3',
    appId: 'truss',
    critical: true,
    title: 'Truss and frame combinations now use EN 1990:2023 recommended factors by default.',
    body:
      'Finnish and Icelandic design bases are marked recommended: that second-generation annex is not in force here. Snow ψ is no longer stepped at 2.75 kN/m². Combination results will differ from the previous first-generation Finnish set.',
  },
  {
    date: '2026-10-04',
    version: '0.3',
    appId: 'truss',
    critical: true,
    title: 'Roof imposed load now defaults to category H (ψ = 0).',
    body:
      'Category E (storage, solar panels, or suspended services) is opt-in. Earlier runs treated the roof imposed load as storage.',
  },
  {
    date: '2026-10-04',
    version: '0.3',
    appId: 'truss',
    title: 'Arched halls, Iceland and Latvia annexes, member-force diagram',
    body:
      'Building shape can be an arched hall: circular or parabolic, beam rib or constant-depth trussed arch, springing at the foundation or on straight columns, with pinned or fixed bases. Cylindrical snow and vault external-pressure coefficients follow the selected annex (Finnish recommendation, Icelandic snow-drift figure, Latvia tagged where the LVS clause is not transcribed). Design basis adds Iceland (snow zones; v_b,0 36 m/s, or 46 m/s on an open coast) and Latvia (enter s_k and v_b,0; the run stops if either is missing). Results include a member-force diagram: axial force on bars, axial force and end moments on chords and columns.',
  },
  {
    date: '2026-10-04',
    version: '0.3',
    appId: 'slab_calculator',
    title: 'Steel-composite slabs and precast creep timing',
    body:
      'Slab family adds a profiled-sheet steel-composite option (EN 1994): sheet thickness, concrete above the ribs, and a longitudinal shear resistance. Zero shear resistance checks the sheet alone in sagging. Precast planks take an age at the topping pour, an age at leveling, ambient humidity, and mean curing temperature so creep uses maturity rather than a fixed 20 °C age. WQ composite beams can be checked with the slab, including construction while propped.',
  },
  {
    date: '2026-10-04',
    version: '0.3',
    appId: 'site_planner',
    title: 'Campus layout and a minimum-land group pack',
    body:
      'Campus mode places datahall rows with a full-depth side corridor, a shared horizontal corridor, and a detached eHouse field on the outer face. A central hall can split the count across chosen wings. In group mode, Min land packs a chosen group count into the smallest rectangle; Max groups still fills the site.',
  },
  {
    date: '2026-10-04',
    version: '0.3',
    appId: 'composite_columns',
    title: 'Symmetric rebar layout for filled CHS and RHS',
    body:
      'Concrete-filled circular and rectangular hollow sections can place a fixed bar count and diameter so ambient and fire utilization is as small as possible. Layouts stay symmetric about at least one axis. Embedment follows the selected fire rating; minimum cover to the inner steel face and clear bar spacing still apply. Spacing constants are the EN 1992-1-1 recommended values and are provisional until the licensed Finnish annex is confirmed.',
  },
  {
    date: '2026-08-05',
    appId: 'truss',
    title: 'Experimental whole-building mode (staged 2D)',
    body:
      'Building mode extends the truss/portal tool to a multi-bay, multi-frame hall model: transverse portal or concentric-braced stability, longitudinal Warren HT bracing and load-path validation, per-frame FEA envelopes, semi-rigid stressed-skin diaphragm checks, and Finnish NAF wind/snow action providers. Coordinate-based ground snow s_k lookup (YM 6/16 map, ±5 km grid) is available for prescreening; a separate Fi snow contour digitizer supports map maintenance. Results are experimental and for prescreening only — not for certificate-grade design without independent review.',
  },
  {
    date: '2026-08-05',
    appId: 'workspaces',
    title: 'Workspace login for saved calculations',
    body:
      'Sign in with a username (no password) to attach saved analyses to a personal workspace across browsers and devices. Calculation history on the dashboard and in each tool is scoped to the active workspace; beam, composite column, console, foundation, slab, truss, and site planner saves all use the same identity.',
  },
  {
    date: '2026-08-05',
    appId: 'site_planner',
    title: 'Site planner: DXF boundary layout and module optimization',
    body:
      'New tool for placing standardized site modules (M1/M2 production units, M3/M4 infrastructure) inside an imported DXF site boundary or on a map canvas. Optimizers pack modules in group, hatch, or polygon-decomposition modes with setbacks, corridors, and manual edits; layouts can be exported to DXF and saved to your workspace.',
  },
  {
    date: '2026-08-05',
    appId: 'foundations',
    title: 'Ground-facing slab on pile grid',
    body:
      'New foundation type: a ground-facing slab supported on a regular pile grid, solved as an orthotropic plate on Winkler springs with separate ULS scenarios (uniform live plus self-weight, concentrated patch loads at centre/edge/corner envelope positions, and line loads along selected slab edges). Includes EC2 slab flexure/shear checks and pile-head structural verification.',
  },
  {
    date: '2026-08-05',
    appId: 'foundations',
    title: 'Foundation engine refinements',
    body:
      'Pile-cap EC2 design, consolidated EC7 design basis, and updates across lateral p-y analysis, liquefaction, seismic EC8, site response, py-curve generation, pile BEF head fixity, and standard foundation catalog data. Ground-facing slab shares the new slab load, support, and plate-solver building blocks with clearer soil-unit handling.',
  },
  {
    date: '2026-08-05',
    appId: 'slab_calculator',
    title: 'Orthotropic plate FEM, tributary loads, and slab–beam coupling',
    body:
      'Slab analysis uses orthotropic plate rigidities (D11/D22/D66) with finite-difference biharmonic moments, nodal tributary areas for load distribution to supporting beams, and an improved nonlinear slab–beam contact iteration. Added support-line geometry, WQ composite rigidity, Finnish design helpers, cast-in deflection updates, and expanded verification against edge contact and tendon stress cases.',
  },
  {
    date: '2026-08-05',
    appId: 'truss',
    title: 'Portal frame solver, EC3 checks, and joint precheck',
    body:
      'Major refresh of the 2D frame/truss solver and optimization path: stiffer portal-frame analysis, expanded EC3 member and joint checks, earlier truss joint precheck before full auto-optimization, and section-catalog updates. Building on the earlier portal-frame TrussCalculator fix.',
  },
  {
    date: '2026-04-16',
    appId: 'foundations',
    title: 'Pile laterals: distributed BEF check and continuous head fixity',
    body:
      'Optional distributed lateral analysis (nonlinear p-y on a beam-on-Winkler pile model, with kinematics and N-M utilization) complements the existing methods. The pile BEF solver applies head restraint with penalty springs scaled linearly with fixity from 0 (no head springs) to 1 (stiff head); reference stiffness scales with EI and length so intermediate fixity stays compliant. Head shear and moment are applied on the head degrees of freedom. The legacy py_curve lateral plot path still uses the older analytical deflection envelope for visualization.',
  },
  {
    date: '2026-04-16',
    appId: 'beam_formulae',
    title: 'Concrete nonlinear beam: EC2 M–κ and prestress',
    body:
      'Nonlinear concrete beam analysis (M–κ) aligned with EN 1992-1-1 using Finnish NA material data: M_cr accounts for bonded prestress precompression and correct sagging lever arm; tension stiffening sigma_sr includes prestressing steel per EC2; prestress is represented as equivalent end moments in the beam FEA; the M–κ table is cut after the global moment peak so post-peak softening is not used for secant stiffness.',
  },
  {
    date: '2026-04-13',
    appId: 'truss',
    title: 'Truss: Eurocode vs AISC 360-16 LRFD capacity checks',
    body: 'Design code toggle on the truss form; backend runs EN 1993 member/joint checks or AISC LRFD (including cold-formed HSS t_des = 0.93 t).',
  },
  {
    date: '2026-04-12',
    appId: 'truss',
    title: 'Truss & frame (steel): new analysis tool',
    body: 'Added a truss / portal-style steel calculator with structural input, force and deflection results, and related design checks.',
  },
  {
    date: '2026-04-12',
    appId: 'beam_formulae',
    title: 'Beam calculator: 7-DOF (Vlasov / warping torsion) solver',
    body: 'Extended the beam tool with a seven-degree-of-freedom beam model for warping and torsion effects alongside the existing analyses.',
  },
];
